import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { GetApp, IosShare } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../api/authService';
import { LoginRequest } from '../types/types';
// Rimosso HomeButton dalla pagina di Login come richiesto

// Interfaccia per l'evento BeforeInstallPrompt
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginRequest>({
    username: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  // Stato per PWA install prompt
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [, setIsInstallable] = useState<boolean>(false);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [, setIsAndroid] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showInstallDialog, setShowInstallDialog] = useState<boolean>(false);

  // Controlla se l'app è già in modalità standalone (PWA installata)
  useEffect(() => {
    const checkStandalone = () => {
      const isStandaloneMode = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');
      setIsStandalone(isStandaloneMode);
    };
    
    // Rileva iOS
    const checkIsIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };

    // Rileva Android
    const checkIsAndroid = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /android/.test(userAgent);
    };

    // Rileva dispositivo mobile
    const checkIsMobile = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    };
    
    checkStandalone();
    setIsIOS(checkIsIOS());
    setIsAndroid(checkIsAndroid());
    setIsMobile(checkIsMobile());

    // Ascolta l'evento beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Ascolta quando l'app viene installata
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Funzione per installare la PWA
  const handleInstallClick = async () => {
    // Se abbiamo il prompt nativo (Android Chrome), usalo
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        
        setDeferredPrompt(null);
        setIsInstallable(false);
      } catch (error) {
        console.error('Errore durante l\'installazione:', error);
      }
      return;
    }

    // Altrimenti mostra le istruzioni (iOS o Android senza prompt)
    setShowInstallDialog(true);
  };

  // Mostra il pulsante su mobile (Android o iOS) e non in standalone
  const showInstallButton = isMobile && !isStandalone;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.login(credentials);
      
      if (response.passwordChangeRequired) {
          navigate('/cambio-password', { state: { username: response.username } });
          return;
      }

      authService.storeAuthData(response);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src={process.env.PUBLIC_URL + '/logo192.png'} alt="Gioielli di Valenza" style={{ width: 144, height: 144, objectFit: 'contain', opacity: 0.9 }} />
          </Box>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            App Orari
          </Typography>
          <Typography component="h2" variant="h6" align="center" sx={{ mb: 3 }}>
            Accesso
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={credentials.username}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={credentials.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>

            {/* Pulsante Installa App - visibile solo da mobile e non in PWA */}
            {showInstallButton && (
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={isIOS ? <IosShare /> : <GetApp />}
                onClick={handleInstallClick}
                sx={{ 
                  mb: 2,
                  borderColor: '#9c27b0',
                  color: '#9c27b0',
                  '&:hover': {
                    borderColor: '#7b1fa2',
                    backgroundColor: 'rgba(156, 39, 176, 0.04)'
                  }
                }}
              >
                Installa App
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Dialog istruzioni per installazione PWA */}
      <Dialog open={showInstallDialog} onClose={() => setShowInstallDialog(false)}>
        <DialogTitle>
          {isIOS ? 'Installa App su iPhone' : 'Installa App su Android'}
        </DialogTitle>
        <DialogContent>
          {isIOS ? (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Per installare l'app sulla schermata Home del tuo iPhone:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  1. Tocca l'icona <IosShare sx={{ verticalAlign: 'middle', fontSize: 20 }} /> (Condividi) in basso
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  2. Scorri e seleziona <strong>"Aggiungi a Home"</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  3. Tocca <strong>"Aggiungi"</strong> in alto a destra
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Per installare l'app sulla schermata Home del tuo dispositivo Android:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  1. Tocca il menu <strong>⋮</strong> (tre puntini) in alto a destra nel browser
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  2. Seleziona <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  3. Conferma l'installazione
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallDialog(false)} color="primary">
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>

      {/* HomeButton rimosso dalla pagina di Login */}
    </Container>
  );
};

export default Login;
